import React from 'react';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../../redux/snackbarSlice';

/**
 * CopyReplaceConfirmModal Component
 * Modal popup for confirming copy & replace operation
 * Based on old implementation from ProcedureCopyPreview.js
 */
const CopyReplaceConfirmModal = ({
  sourceDocument,
  sourceNode,
  copyMode,
  targetDocument,
  pastePosition,
  comment,
  onCommentChange,
  onClose,
  onSave,
  isLoading,
  breadcrumbText
}) => {
  const dispatch = useDispatch();

  const handleSave = () => {
    if (!comment || !comment.trim()) {
      dispatch(showSnackbar({ message: 'Comment is mandatory.', type: 'error' }));
      return;
    }
    onSave();
  };

  // Build tree path for "For" display based on breadcrumbText when available, fallback to sourceNode properties
  const getTreePath = () => {
    if (copyMode !== 'node' || !sourceNode) return '';
    
    // Use breadcrumbText if available - this contains the correct extracted path like "Section 2 > Step 2 > Substep 2"
    if (breadcrumbText) {
      // For video nodes, the breadcrumbText should include the full path, so just append the node title
      if (['attachment','video','rich-text','form','canva','canvas'].includes(sourceNode.nodeType)) {
        // Check if breadcrumbText already ends with the node title (some cases it might)
        if (breadcrumbText.endsWith(` > ${sourceNode.nodeTitle}`)) {
          return breadcrumbText;
        } else {
          // Append the node title to the breadcrumb path
          const fullPath = `${breadcrumbText} > ${sourceNode.nodeTitle}`;
          return fullPath;
        }
      } else {
        // For other node types (substep, step), use breadcrumbText directly
        return breadcrumbText;
      }
    }
    
    // Fallback to building path from sourceNode properties if no breadcrumbText
    let path = `${sourceNode.nodeTitle}`;
    
    // For substep, add Step X before the substep
    if (sourceNode.nodeType === 'substep' && sourceNode.globalStepNumber) {
      // Add section if available
      if (sourceNode.globalSectionNumber) {
        path = `Section ${sourceNode.globalSectionNumber} > Step ${sourceNode.globalStepNumber} > ${path}`;
      } else {
        path = `Step ${sourceNode.globalStepNumber} > ${path}`;
      }
    }
    // For child nodes (attachment, video, rich-text, form, canvas)
    else if (['attachment','video','rich-text','form','canva','canvas'].includes(sourceNode.nodeType) && sourceNode.globalStepNumber) {
      // Check if this is a substep child using the new isSubstepChild flag
      if (sourceNode.isSubstepChild && sourceNode.substepIndex !== null && sourceNode.substepIndex !== undefined) {
        const substepLetter = String.fromCharCode(65 + (sourceNode.substepIndex || 0));
        if (sourceNode.globalSectionNumber) {
          path = `Section ${sourceNode.globalSectionNumber} > Step ${sourceNode.globalStepNumber} > Substep ${sourceNode.globalStepNumber}${substepLetter} > ${path}`;
        } else {
          path = `Step ${sourceNode.globalStepNumber} > Substep ${sourceNode.globalStepNumber}${substepLetter} > ${path}`;
        }
      }
      // Fallback to old logic using substepId
      else if (sourceNode.substepId && sourceNode.substepIndex !== null && sourceNode.substepIndex !== undefined) {
        const substepLetter = String.fromCharCode(65 + (sourceNode.substepIndex || 0));
        if (sourceNode.globalSectionNumber) {
          path = `Section ${sourceNode.globalSectionNumber} > Step ${sourceNode.globalStepNumber} > Substep ${sourceNode.globalStepNumber}${substepLetter} > ${path}`;
        } else {
          path = `Step ${sourceNode.globalStepNumber} > Substep ${sourceNode.globalStepNumber}${substepLetter} > ${path}`;
        }
      } 
      // Otherwise it's a step child - add Section X > Step X
      else {
        if (sourceNode.globalSectionNumber) {
          path = `Section ${sourceNode.globalSectionNumber} > Step ${sourceNode.globalStepNumber} > ${path}`;
        } else {
          path = `Step ${sourceNode.globalStepNumber} > ${path}`;
        }
      }
    }
    
    return path;
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content_addsection">
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <h4
            style={{
              marginLeft: "23px",
              fontSize: "18px",
              color: "#333333",
            }}
          >
            {copyMode === 'node' && sourceNode 
              ? `Confirm Copy of ${sourceNode.nodeTitle} from ${sourceDocument.partNumber}, Rev ${sourceDocument.revision}`
              : `Confirm Copy & Replace of ${sourceDocument.type} ${sourceDocument.partNumber}, Rev ${sourceDocument.revision}`
            }
          </h4>
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              right: "10px",
              top: "10px",
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#666",
            }}
            disabled={isLoading}
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div
          style={{ padding: "24px", marginTop: "-28px", color: "#333333" }}
        >
          <h4 style={{ fontSize: "14px" }}>
            {copyMode === 'node' && sourceNode
              ? `For ${sourceDocument.partNumber} > Rev ${sourceDocument.revision}${getTreePath() ? ' > ' + getTreePath() : ''}`
              : `For ${sourceDocument.partNumber} > Rev ${sourceDocument.revision}`
            }
          </h4>

          <p style={{ fontSize: "14px", color: "#333333" }}>
            <strong>Editor:</strong>{" "}
            <span style={{ marginLeft: "10px" }}>
              {localStorage.getItem("user_name")}
            </span>
          </p>

          <label
            className="modal-label"
            style={{ fontSize: "14px", color: "#333333" }}
          >
            Comment
            <span className="required" style={{ marginLeft: "-1px" }}>
              *
            </span>
          </label>
          <textarea
            className="modal-textarea"
            placeholder="Enter your comments here..."
            value={comment || ''}
            onChange={(e) => onCommentChange(e.target.value)}
            style={{
              width: "100%",
              minHeight: "100px",
              padding: "8px",
              border: "1px solid #ddd",
              borderRadius: "4px",
            }}
            disabled={isLoading}
          ></textarea>
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            padding: "16px 24px",
            borderTop: "1px solid #e0e0e0",
            marginTop: "-10px",
            position: "relative",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 16px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              background: "#6c757d",
              color: "#e9ecef",
              cursor: isLoading ? "not-allowed" : "pointer",
            }}
            disabled={isLoading}
          >
            Close
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: "8px 16px",
              border: "1px solid #007bff",
              borderRadius: "4px",
              background: "#007bff",
              color: "white",
              cursor: isLoading ? "not-allowed" : "pointer",
              opacity: isLoading ? 0.7 : 1,
            }}
            disabled={isLoading}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default CopyReplaceConfirmModal;

